/**
 * View-mode canvas navigation: user zoom factor and pan offsets (CSS px on the stage).
 * Gestures live on {@link UppFloorPlanStageComponent}; this module holds clamp/math helpers.
 */

/**
 * Min / max for the multiplicative user zoom applied after fit-to-frame. Min is **1** (100%, the fit-to-frame
 * size): zooming out further would shrink the room away from the frame edges with nothing gained, and it keeps
 * "at 100%" as the unambiguous boundary where one-finger drag hands off from pan to the floor-swipe gesture
 * (see `_installViewNavigation` in `upp-floor-plan-stage.component.ts` and `SWIPE_ZOOM_BLOCK_ABOVE`
 * in `upp-tab-tables.ts`).
 */
export const FLOOR_PLAN_USER_ZOOM_MIN = 1;
export const FLOOR_PLAN_USER_ZOOM_MAX = 4;
/** Step used by {@link zoomIn} / {@link zoomOut} and discrete wheel ticks. */
export const FLOOR_PLAN_USER_ZOOM_STEP = 1.1;

export type FloorPlanViewNavState = {
  zoomFactor: number;
  panOffsetXPx: number;
  panOffsetYPx: number;
};

/** Payload emitted when the user changes zoom or pan in view mode. */
export type UppKonvaTransform = FloorPlanViewNavState;

export function clampFloorPlanUserZoomFactor(z: number): number {
  if (!Number.isFinite(z)) {
    return 1;
  }
  return Math.min(FLOOR_PLAN_USER_ZOOM_MAX, Math.max(FLOOR_PLAN_USER_ZOOM_MIN, z));
}

/** Multiplies the current zoom by {@link FLOOR_PLAN_USER_ZOOM_STEP} (in) or its inverse (out). */
export function stepFloorPlanUserZoom(current: number, direction: 'in' | 'out'): number {
  const next =
    direction === 'in'
      ? current * FLOOR_PLAN_USER_ZOOM_STEP
      : current / FLOOR_PLAN_USER_ZOOM_STEP;
  return clampFloorPlanUserZoomFactor(next);
}

/**
 * Wheel delta → zoom multiplier. Negative `deltaY` zooms in (typical trackpad / mouse wheel).
 */
export function wheelDeltaToZoomMultiplier(deltaY: number): number {
  if (!Number.isFinite(deltaY) || deltaY === 0) {
    return 1;
  }
  const ticks = Math.max(-3, Math.min(3, -deltaY / 100));
  return Math.pow(FLOOR_PLAN_USER_ZOOM_STEP, ticks);
}

/**
 * Adjusts pan so the room point under `focalStagePx` stays fixed when zoom (and thus fit origin) changes.
 * `mapStageToRoomCm` / `mapRoomCmToStage` use the transform **before** / **after** the zoom change.
 */
export function panDeltaToKeepFocalStagePoint(
  focalStagePx: { x: number; y: number },
  mapStageToRoomCm: (x: number, y: number) => { xCm: number; yCm: number },
  mapRoomCmToStage: (xCm: number, yCm: number) => { x: number; y: number },
): { dx: number; dy: number } {
  const room = mapStageToRoomCm(focalStagePx.x, focalStagePx.y);
  const after = mapRoomCmToStage(room.xCm, room.yCm);
  return {
    dx: focalStagePx.x - after.x,
    dy: focalStagePx.y - after.y,
  };
}
